import {useCallback} from "react";

import {GuideStateApi} from "@repo/guide-state";
import type {AIGuideMetadata} from "@repo/guide-types";
import {Errors, KAError} from "@repo/logging";

import type {GuideComponents} from "../component-types.ts";
import {useSendGuideItem} from "../internal/use-send-guide-item.ts";
import {langfuseSessionURL} from "../utils/traces.ts";

type Result<TOutput> =
    | {ok: false; error: Error}
    | {ok: true; output: TOutput; interactionId: string; sessionUrl: string};

interface RunGuideComponentOptions {
    generateVoice?: boolean;
}

export default function useRunGuideComponent<
    ComponentName extends keyof GuideComponents,
    ComponentOutput = GuideComponents[ComponentName]["output"],
    ComponentInput = GuideComponents[ComponentName]["input"],
>(componentName: ComponentName) {
    const sendGuideItem = useSendGuideItem();

    const runComponent: (
        input: ComponentInput,
        options?: RunGuideComponentOptions,
    ) => Promise<Result<ComponentOutput>> = useCallback(
        async (input: ComponentInput, options?: RunGuideComponentOptions) => {
            GuideStateApi.set("isLoading", true);

            let lastMetadata: AIGuideMetadata | null = null;
            let lastError: Error | null = null;

            try {
                const {generateVoice} = options ?? {};

                await sendGuideItem(
                    "entry_component",
                    {
                        entryComponent: {
                            name: componentName,
                            input: input ?? {},
                        },
                    },
                    {
                        generateVoice,
                        onResponseComplete(metadata) {
                            lastMetadata = metadata;
                        },
                        onResponseFailed(err) {
                            lastError = err;
                        },
                    },
                );

                if (lastError != null) {
                    throw lastError;
                }

                if (lastMetadata == null) {
                    throw new KAError("No metadata received", Errors.Internal);
                }
            } catch (err) {
                return {ok: false, error: err as Error};
            } finally {
                GuideStateApi.set("isLoading", false);
            }

            const {entryComponentOutput, threadId, interactionId} =
                lastMetadata;

            return {
                ok: true,
                output: entryComponentOutput,
                interactionId,
                sessionUrl: langfuseSessionURL(threadId),
            };
        },
        [sendGuideItem, componentName],
    );

    return runComponent;
}
